import { keyProofBinding, keypairFromSecret, signKeyProof, unwrapKey, wrapKey, type KeyCopy, type KeyProofOp } from "@timecapsule/crypto";
import { blindPin, finalizePin } from "@timecapsule/crypto/oprf";
import { api, ApiError, type KeyProof } from "./api";
import type { DeviceKey } from "./storage";
import { msg } from "./i18n";

/**
 * Key recovery v2 in the browser (PLAN.md 4.6). These functions combine the crypto package,
 * the passkey PRF output and the server's OPRF; pages only collect PIN, phrase and clicks.
 */

/**
 * Signs a fresh server challenge for one operation and its content (PLAN.md 4.13 R2): the server
 * learns that this browser holds the key and agrees to exactly this request, nothing more.
 * `bindParts` must match what the server recomputes (`keyProofBinding` in the crypto package).
 */
export async function prove(secretKey: Uint8Array, op: KeyProofOp, bindParts: readonly string[] | null = null): Promise<KeyProof> {
  const challenge = await api.keyChallenge(op, keyProofBinding(bindParts));
  return { challenge, proof: signKeyProof(challenge, secretKey) };
}

/**
 * Makes this key the member's active identity on the server. `currentKey` is the active key and
 * is required when the account already has one (PLAN.md 4.11 H1): two proofs, two challenges.
 */
export async function registerKey(secretKey: Uint8Array, currentKey?: Uint8Array | null): Promise<void> {
  const pubkey = keypairFromSecret(secretKey).publicKey;
  await api.registerKey(await prove(secretKey, "register"), currentKey ? await prove(currentKey, "switch", [pubkey]) : undefined);
}

/** Google re-link: this login takes over the member whose active identity is this key. */
export async function relinkLogin(secretKey: Uint8Array): Promise<void> {
  await api.relink(await prove(secretKey, "relink"));
}

/** F(k, PIN) for a key this browser already holds: proven, so the server does not count it. `keyId` names the server's OPRF key. */
async function oprfAsKeyHolder(pin: string, secretKey: Uint8Array): Promise<{ output: Uint8Array; keyId: string }> {
  const { request, state } = await blindPin(pin);
  const { response, key_id } = await api.oprf(request, await prove(secretKey, "oprf", [request]));
  return { output: await finalizePin(state, response), keyId: key_id };
}

/**
 * Device copy plus, if wanted, a new server copy (passkey PRF + OPRF-hardened PIN). The server
 * copy replaces any older one and starts with a fresh attempt counter. Without passkey PRF
 * (`passkey` null) the device keeps the key unencrypted and there is no server copy at all
 * (PLAN.md 4.11 H2): the phrase is the way to another device.
 */
export async function protectKey(opts: {
  secretKey: Uint8Array;
  pin: string | null;
  passkey: { credentialId: string; prf: Uint8Array } | null;
  serverCopy: boolean;
}): Promise<DeviceKey> {
  const { secretKey, pin, passkey } = opts;
  const pubkey = keypairFromSecret(secretKey).publicKey;
  if (!passkey || !pin) return { kind: "plain", pubkey, secretKey };
  if (opts.serverCopy) {
    const oprf = await oprfAsKeyHolder(pin, secretKey);
    const copy = await wrapKey(secretKey, "oprf-prf", { prf: passkey.prf, oprf: oprf.output }, passkey.credentialId);
    await api.saveServerCopy(copy, oprf.keyId, await prove(secretKey, "backup", [oprf.keyId, JSON.stringify(copy)]));
  }
  return { kind: "prf-pin", pubkey, copy: await wrapKey(secretKey, "prf-pin", { prf: passkey.prf, pin }, passkey.credentialId) };
}

export class WrongPinError extends Error {
  constructor(readonly attemptsLeft: number | null) {
    super(attemptsLeft === 0 ? msg().errors.wrongPinBlocked : msg().errors.wrongPin(attemptsLeft));
    this.name = "WrongPinError";
  }
}

/**
 * Opens the server copy with passkey PRF + PIN. One counted attempt. On success the browser
 * proves the key, which resets the counter. Throws WrongPinError or ApiError 423 (locked).
 */
export async function openServerCopy(copy: KeyCopy, pin: string, prf: Uint8Array): Promise<Uint8Array> {
  const { request, state } = await blindPin(pin);
  const res = await api.oprf(request);
  const oprf = await finalizePin(state, res.response);
  let secretKey: Uint8Array;
  try {
    secretKey = await unwrapKey(copy, { prf, oprf });
  } catch {
    throw new WrongPinError(res.attempts_left ?? null);
  }
  await api.keyUnlocked(await prove(secretKey, "unlocked"));
  return secretKey;
}

export function isLocked(err: unknown): boolean {
  return err instanceof ApiError && err.status === 423;
}

/**
 * Short form of a pubkey for people to compare (PLAN.md 4.10, 4.11 H1): the first 16 hex
 * characters in four groups, "3f9a 1c2e 88b0 c21e" (64 bits; the review asked for more than the
 * earlier 8). The full key is shown where a member confirms a changed key.
 */
export function fingerprint(pubkey: string): string {
  return pubkey.slice(0, 16).match(/.{4}/g)!.join(" ");
}

/** Default minutes for "Unlock for … minutes" (PLAN.md 4.6; since 4b step 2g a choice, not a fixed value). */
export const UNLOCK_MINUTES = 15;
export const UNLOCK_CHOICES = [5, 15, 30, 60];
